import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Navbar from '../../components/navBar/navBar';
import ScopeFilters from '../../components/ScopeFilters/ScopeFilters';
import { apiList, encrypt, verifyLogin } from '../../helpers';
import LoanMapCanvas from './LoanMapCanvas';
import LoanCard from './LoanCard';
import PhotoLocationsPanel from './PhotoLocationsPanel';
import {
  ACTIVITY, ACTIVITY_BY_KEY, COL, FLAGS, flagOf, idleText,
} from './mapMeta';

// Loan map: every loan the user may see, coloured by how recently photos
// were uploaded, ringed when something needs action (off-site photos,
// activities awaiting review, open disbursement request). The left panel
// always lists the loans in view (or search results); the selected loan's
// details open in a pop-up on its pin.

const LIST_LIMIT = 150;
const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');

const within = (b, row) => b && row[COL.lat] <= b.north && row[COL.lat] >= b.south
  && (b.west <= b.east
    ? row[COL.lng] >= b.west && row[COL.lng] <= b.east
    : row[COL.lng] >= b.west || row[COL.lng] <= b.east);

// Things to act on first, then the most recently active.
const priority = (row) => (row[COL.offsite] > 0 ? 4 : 0) + (row[COL.review] > 0 ? 2 : 0)
  + (row[COL.request] > 0 ? 1 : 0);
const idleOf = (row) => (row[COL.idleDays] === null ? 1e9 : row[COL.idleDays]);
const byPriority = (a, b) => priority(b) - priority(a) || idleOf(a) - idleOf(b);

export default function LoanMaps(props) {
  const [filters, setFilters] = useState({ branches: [], states: [] });
  const [data, setData] = useState({ rows: [], unplaced: { missing: 0, invalid: 0 } });
  const [dataKey, setDataKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activityOn, setActivityOn] = useState(
    ACTIVITY.reduce((m, a) => ({ ...m, [a.key]: true }), {}),
  );
  const [flagsOn, setFlagsOn] = useState([]);
  const [search, setSearch] = useState('');
  const [bounds, setBounds] = useState(null);
  const [selected, setSelected] = useState(null);
  const [focusKey, setFocusKey] = useState(0);
  const [photosFor, setPhotosFor] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const { session } = verifyLogin();
    axios.get(apiList.MAP_LOANS, {
      headers: { session, permissionConfig: 'Property Details', feature: 'view' },
      params: { branches: filters.branches.join(','), states: filters.states.join(',') },
    })
      .then((r) => {
        if (!active) return;
        setData(r.data.data);
        setDataKey((k) => k + 1);
        setSelected(null);
      })
      .catch(() => { if (active) setError('Could not load the map data. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters]);

  const counts = useMemo(() => {
    const c = { activity: {}, flag: {} };
    data.rows.forEach((row) => {
      c.activity[row[COL.activity]] = (c.activity[row[COL.activity]] || 0) + 1;
      FLAGS.forEach((f) => { if (row[f.col] > 0) c.flag[f.key] = (c.flag[f.key] || 0) + 1; });
    });
    return c;
  }, [data]);

  // Pins currently shown (legend + flag filters).
  const points = useMemo(() => data.rows.filter((row) => activityOn[row[COL.activity]]
    && (!flagsOn.length || FLAGS.some((f) => flagsOn.includes(f.key) && row[f.col] > 0))),
  [data, activityOn, flagsOn]);

  const query = search.trim().toLowerCase();
  const list = useMemo(() => {
    if (query.length > 1) {
      return points.filter((row) => String(row[COL.loanNumber] || '').toLowerCase().includes(query)
        || String(row[COL.custName] || '').toLowerCase().includes(query));
    }
    return points.filter((row) => within(bounds, row)).sort(byPriority);
  }, [points, bounds, query]);

  const toggleFlag = (key) => setFlagsOn((on) => (on.includes(key)
    ? on.filter((k) => k !== key) : [...on, key]));
  const pick = (row, zoom) => {
    setSelected(row);
    if (zoom) setFocusKey((k) => k + 1);
  };
  const unplaced = data.unplaced.missing + data.unplaced.invalid;

  verifyLogin();
  return (
    <>
      {/* Outside .lm-page: its flex row must hold only the panel and map. */}
      <Navbar MainProps={props} />
      <div className='lm-page'>
        <PhotoLocationsPanel
          loan={photosFor}
          onClose={() => setPhotosFor(null)}
        />

        <aside className='lm-panel'>
          <div className='lm-panel-top'>
            <ScopeFilters value={filters} onChange={setFilters} />
            <input
              className='lm-search'
              type='search'
              placeholder='Search loan number or customer'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label='Search loans on the map'
            />

            <div className='lm-section-title'>Activity (last photo upload)</div>
            <div className='lm-chips'>
              {ACTIVITY.map((a) => (
                <button
                  key={a.key}
                  type='button'
                  className={`lm-chip ${activityOn[a.key] ? '' : 'off'}`}
                  title={`${a.hint} — click to ${activityOn[a.key] ? 'hide' : 'show'}`}
                  aria-pressed={activityOn[a.key]}
                  onClick={() => setActivityOn((on) => ({ ...on, [a.key]: !on[a.key] }))}
                >
                  <span
                    className='lm-dot'
                    style={{ background: a.color, boxShadow: a.stroke ? `0 0 0 1px ${a.stroke}` : undefined }}
                  />
                  {a.label}
                  <strong>{fmt(counts.activity[a.key])}</strong>
                </button>
              ))}
            </div>

            <div className='lm-section-title'>Needs action (pin ring) — click to show only these</div>
            <div className='lm-chips'>
              {FLAGS.map((f) => (
                <button
                  key={f.key}
                  type='button'
                  className={`lm-chip ${flagsOn.includes(f.key) ? 'on-flag' : ''}`}
                  style={flagsOn.includes(f.key) ? { color: f.color } : undefined}
                  title={f.hint}
                  aria-pressed={flagsOn.includes(f.key)}
                  onClick={() => toggleFlag(f.key)}
                >
                  <span className='lm-dot ring' style={{ borderColor: f.color }} />
                  <span style={{ color: '#1F2733' }}>{f.label}</span>
                  <strong style={{ color: '#1F2733' }}>{fmt(counts.flag[f.key])}</strong>
                </button>
              ))}
            </div>

            <div className='lm-summary'>
              <strong>{fmt(points.length)}</strong>
              {` of ${fmt(data.rows.length)} loans on the map`}
              {unplaced ? ` · ${fmt(unplaced)} can't be placed (no valid location)` : ''}
            </div>
          </div>

          <div className='lm-panel-body'>
            <>
              <div className='lm-list-head'>
                <span>{query.length > 1 ? 'Search results' : 'Loans in view'}</span>
                <span>{fmt(list.length)}</span>
              </div>
              {error ? <div className='lm-empty'>{error}</div> : null}
              {!error && !list.length ? (
                <div className='lm-empty'>
                  {loading ? 'Loading loans…' : 'No loans here. Zoom out or change the filters.'}
                </div>
              ) : null}
              {list.slice(0, LIST_LIMIT).map((row) => {
                const activity = ACTIVITY_BY_KEY[row[COL.activity]];
                const flag = flagOf(row);
                return (
                  <button
                    key={row[COL.loanId]}
                    type='button'
                    className={`lm-row ${selected && selected[COL.loanId] === row[COL.loanId] ? 'selected' : ''}`}
                    aria-current={selected && selected[COL.loanId] === row[COL.loanId] ? 'true' : undefined}
                    onClick={() => pick(row, true)}
                  >
                    <span
                      className='lm-dot'
                      style={{
                        background: activity.color,
                        boxShadow: `0 0 0 ${flag ? 2 : 1}px ${flag ? flag.color : (activity.stroke || 'rgba(16,36,70,0.25)')}`,
                      }}
                    />
                    <span className='lm-row-main'>
                      <span className='lm-row-title'>
                        <span>{row[COL.loanNumber] || 'N/A'}</span>
                        <span>{row[COL.progress] === null ? '' : `${row[COL.progress]}%`}</span>
                      </span>
                      <span className='lm-row-sub' style={{ display: 'block' }}>
                        {`${row[COL.custName] || ''} · ${idleText(row[COL.idleDays])}`}
                      </span>
                      {priority(row) ? (
                        <span className='lm-flags'>
                          {row[COL.offsite] > 0 ? <span className='lm-flag offsite'>{`${row[COL.offsite]} off-site`}</span> : null}
                          {row[COL.review] > 0 ? <span className='lm-flag review'>{`${row[COL.review]} to review`}</span> : null}
                          {row[COL.request] > 0 ? <span className='lm-flag request'>Open request</span> : null}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
              {list.length > LIST_LIMIT ? (
                <div className='lm-more'>
                  {`Showing ${LIST_LIMIT} of ${fmt(list.length)} — zoom in or search to narrow the list.`}
                </div>
              ) : null}
            </>
          </div>
        </aside>

        <section className='lm-map'>
          {loading ? <div className='lm-map-state'>Loading loans…</div> : null}
          <LoanMapCanvas
            points={points}
            dataKey={dataKey}
            selected={selected}
            focusKey={focusKey}
            onSelect={(row) => pick(row, false)}
            onBoundsChange={setBounds}
            onClosePopup={() => setSelected(null)}
            renderPopup={(relayout) => (
              <LoanCard
                key={selected[COL.loanId]}
                row={selected}
                onLayout={relayout}
                onOpenLoan={(loanId) => props.history.push(`/view-loan-details/${encrypt(loanId)}`)}
                onPhotos={(row) => setPhotosFor({
                  loanId: row[COL.loanId], loanNumber: row[COL.loanNumber],
                })}
              />
            )}
          />
        </section>
      </div>
    </>
  );
}
